function isValidCidr(cidr) {
  const parts = cidr.split('/');
  if (parts.length !== 2) return false;
  const octets = parts[0].split('.');
  if (octets.length !== 4) return false;
  for (const octet of octets) {
    const n = parseInt(octet, 10);
    if (isNaN(n) || n < 0 || n > 255) return false;
  }
  const prefix = parseInt(parts[1], 10);
  if (isNaN(prefix) || prefix < 0 || prefix > 32) return false;
  return true;
}

function sanitize(name) {
  return name.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/^[^a-zA-Z]/, 'r_$&') || 'unnamed';
}

function escapeHclString(str) {
  return str.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function findParentVnet(subnet, vnetNodes, edges, errors) {
  if (subnet.parentId) {
    const parent = vnetNodes.find((v) => v.id === subnet.parentId);
    if (parent) return parent;
    errors.push(`Subnet "${subnet.data.name}" has parentId "${subnet.parentId}" but no matching VNet found`);
    return undefined;
  }
  const edge = edges.find((e) => e.target === subnet.id);
  if (edge) {
    const parent = vnetNodes.find((v) => v.id === edge.source);
    if (parent) return parent;
    errors.push(`Subnet "${subnet.data.name}" has edge from "${edge.source}" but no matching VNet found`);
    return undefined;
  }
  if (vnetNodes.length === 1) {
    return vnetNodes[0];
  }
  errors.push(`Subnet "${subnet.data.name}" is missing parent VNet — drag it inside a VNet or connect with an edge`);
  return undefined;
}

function findParentRg(subnet, vnetNodes, rgNodes, edges, errors) {
  const parentVnet = findParentVnet(subnet, vnetNodes, edges, errors);
  if (!parentVnet) {
    return undefined;
  }
  if (parentVnet.parentId) {
    const parent = rgNodes.find((r) => r.id === parentVnet.parentId);
    if (parent) return parent;
    errors.push(`VNet "${parentVnet.data.name}" has parentId "${parentVnet.parentId}" but no matching resource group found`);
    return undefined;
  }
  if (parentVnet.data.resourceGroupName) {
    const parent = rgNodes.find((r) => r.data.name === parentVnet.data.resourceGroupName);
    if (parent) return parent;
    errors.push(`VNet "${parentVnet.data.name}" references resource group "${parentVnet.data.resourceGroupName}" but it was not found`);
    return undefined;
  }
  if (rgNodes.length === 1) {
    return rgNodes[0];
  }
  errors.push(`VNet "${parentVnet.data.name}" is missing parent resource group — drag it inside a Resource Group or set Resource Group in properties`);
  return undefined;
}

function findParentRgForNode(node, rgNodes, edges, errors) {
  if (node.parentId) {
    const parent = rgNodes.find((r) => r.id === node.parentId);
    if (parent) return parent;
  }
  if (node.data.resourceGroupName) {
    const parent = rgNodes.find((r) => r.data.name === node.data.resourceGroupName);
    if (parent) return parent;
  }
  const edge = edges.find((e) => e.target === node.id && rgNodes.some((r) => r.id === e.source));
  if (edge) {
    return rgNodes.find((r) => r.id === edge.source);
  }
  if (rgNodes.length === 1) {
    return rgNodes[0];
  }
  errors.push(`"${node.data.name}" has no resource group — drag it inside a Resource Group or set Resource Group in properties`);
  return undefined;
}

function findParentSubnetForVm(vm, vnetNodes, subnetNodes, edges) {
  if (vm.parentId && subnetNodes.some((s) => s.id === vm.parentId)) {
    return subnetNodes.find((s) => s.id === vm.parentId);
  }
  const edge = edges.find((e) => e.target === vm.id && subnetNodes.some((s) => s.id === e.source));
  if (edge) {
    return subnetNodes.find((s) => s.id === edge.source);
  }
  if (subnetNodes.length === 1) return subnetNodes[0];
  return undefined;
}

export function generateTerraform(nodes, edges) {
  const errors = [];
  const lines = [];

  lines.push(`terraform {
  required_providers {
    azurerm = {
      source  = "hashicorp/azurerm"
      version = "~> 4.0"
    }
  }
}

provider "azurerm" {
  features {}
}
`);

  const rgNodes = nodes.filter((n) => n.data && n.data.type === 'resource-group');
  const vnetNodes = nodes.filter((n) => n.data && n.data.type === 'vnet');
  const subnetNodes = nodes.filter((n) => n.data && n.data.type === 'subnet');
  const vmNodes = nodes.filter((n) => n.data && n.data.type === 'virtual-machine');
  const diskNodes = nodes.filter((n) => n.data && n.data.type === 'managed-disk');
  const storageNodes = nodes.filter((n) => n.data && n.data.type === 'storage-account');
  const nsgNodes = nodes.filter((n) => n.data && n.data.type === 'nsg');
  const routeTableNodes = nodes.filter((n) => n.data && n.data.type === 'route-table');
  const kvNodes = nodes.filter((n) => n.data && n.data.type === 'key-vault');
  const appServiceNodes = nodes.filter((n) => n.data && n.data.type === 'app-service');
  const lbNodes = nodes.filter((n) => n.data && n.data.type === 'load-balancer');
  const peeringEdges = edges.filter((e) => e.label === 'Peering' || e.type === 'peering');

  // Track sanitized names for duplicate detection
  const usedNames = new Map();

  function getUniqueSanitizedName(name) {
    const base = sanitize(name);
    if (!usedNames.has(base)) {
      usedNames.set(base, 1);
      return base;
    }
    let count = usedNames.get(base);
    let candidate = `${base}_${count + 1}`;
    while (usedNames.has(candidate)) {
      count++;
      candidate = `${base}_${count + 1}`;
    }
    usedNames.set(base, count + 1);
    usedNames.set(candidate, 1);
    return candidate;
  }

  for (const rg of rgNodes) {
    const name = getUniqueSanitizedName(rg.data.name);
    const region = rg.data.region || 'eastus';
    lines.push(`resource "azurerm_resource_group" "${name}" {
  name     = "${escapeHclString(rg.data.name)}"
  location = "${escapeHclString(region)}"
}
`);
  }

  for (const vnet of vnetNodes) {
    const name = getUniqueSanitizedName(vnet.data.name);
    const cidr = vnet.data.cidr || '';
    if (cidr && !isValidCidr(cidr)) {
      errors.push(`VNet "${vnet.data.name}" has invalid CIDR: "${cidr}"`);
      continue;
    }

    let rgNode;
    if (vnet.data.resourceGroupName) {
      rgNode = rgNodes.find((r) => r.data.name === vnet.data.resourceGroupName);
      if (!rgNode) {
        errors.push(`VNet "${vnet.data.name}" references resource group "${vnet.data.resourceGroupName}" but it was not found`);
        continue;
      }
    } else if (vnet.parentId) {
      rgNode = rgNodes.find((r) => r.id === vnet.parentId);
      if (!rgNode) {
        errors.push(`VNet "${vnet.data.name}" has parentId "${vnet.parentId}" but no matching resource group found`);
        continue;
      }
    } else if (rgNodes.length === 1) {
      rgNode = rgNodes[0];
    } else {
      errors.push(`VNet "${vnet.data.name}" has no resource group specified — drag it inside a Resource Group or set Resource Group in properties`);
      continue;
    }

    const rgName = sanitize(rgNode.data.name);
    lines.push(`resource "azurerm_virtual_network" "${name}" {
  name                = "${escapeHclString(vnet.data.name)}"
  location            = azurerm_resource_group.${rgName}.location
  resource_group_name = azurerm_resource_group.${rgName}.name
  address_space       = ["${escapeHclString(cidr || '10.0.0.0/16')}"]
}
`);
  }

  for (const subnet of subnetNodes) {
    const name = getUniqueSanitizedName(subnet.data.name);
    const cidr = subnet.data.cidr || '';
    if (cidr && !isValidCidr(cidr)) {
      errors.push(`Subnet "${subnet.data.name}" has invalid CIDR: "${cidr}"`);
      continue;
    }
    const parentVnet = findParentVnet(subnet, vnetNodes, edges, errors);
    const parentRg = findParentRg(subnet, vnetNodes, rgNodes, edges, errors);
    if (!parentVnet || !parentRg) {
      continue;
    }
    const vnetName = sanitize(parentVnet.data.name);
    const rgName = sanitize(parentRg.data.name);
    lines.push(`resource "azurerm_subnet" "${name}" {
  name                 = "${escapeHclString(subnet.data.name)}"
  resource_group_name  = azurerm_resource_group.${rgName}.name
  virtual_network_name = azurerm_virtual_network.${vnetName}.name
  address_prefixes     = ["${escapeHclString(cidr || '10.0.1.0/24')}"]
}
`);
  }

  for (const vm of vmNodes) {
    const name = getUniqueSanitizedName(vm.data.name);
    const sku = vm.data.sku || 'Standard_B2s';
    const rgNode = findParentRgForNode(vm, rgNodes, edges, errors);
    if (!rgNode) continue;
    const rgName = sanitize(rgNode.data.name);

    const subnetNode = findParentSubnetForVm(vm, vnetNodes, subnetNodes, edges);
    const nicName = getUniqueSanitizedName(`${vm.data.name}-nic`);

    if (subnetNode) {
      const snetName = sanitize(subnetNode.data.name);
      lines.push(`resource "azurerm_network_interface" "${nicName}" {
  name                = "${escapeHclString(vm.data.name + '-nic')}"
  location            = azurerm_resource_group.${rgName}.location
  resource_group_name = azurerm_resource_group.${rgName}.name
  ip_configuration {
    name                          = "internal"
    subnet_id                     = azurerm_subnet.${snetName}.id
    private_ip_address_allocation = "Dynamic"
  }
}
`);
    }

    lines.push(`resource "azurerm_linux_virtual_machine" "${name}" {
  name                = "${escapeHclString(vm.data.name)}"
  resource_group_name = azurerm_resource_group.${rgName}.name
  location            = azurerm_resource_group.${rgName}.location
  size                = "${escapeHclString(sku)}"
  admin_username      = "azureadmin"
  network_interface_ids = [${subnetNode ? `azurerm_network_interface.${nicName}.id` : ''}]
  ${subnetNode ? '' : '# No subnet connected — add a network interface manually'}

  os_disk {
    caching              = "ReadWrite"
    storage_account_type = "Standard_LRS"
  }

  source_image_reference {
    publisher = "Canonical"
    offer     = "0001-com-ubuntu-server-jammy"
    sku       = "22_04-lts-gen2"
    version   = "latest"
  }
}
`);
  }

  for (const disk of diskNodes) {
    const name = getUniqueSanitizedName(disk.data.name);
    const diskType = disk.data.diskType || 'Premium_LRS';
    const diskSize = disk.data.diskSize || 128;
    const rgNode = findParentRgForNode(disk, rgNodes, edges, errors);
    if (!rgNode) continue;
    const rgName = sanitize(rgNode.data.name);
    lines.push(`resource "azurerm_managed_disk" "${name}" {
  name                 = "${escapeHclString(disk.data.name)}"
  resource_group_name  = azurerm_resource_group.${rgName}.name
  location             = azurerm_resource_group.${rgName}.location
  storage_account_type = "${escapeHclString(diskType)}"
  disk_size_gb         = ${diskSize}
  create_option        = "Empty"
}
`);
  }

  for (const sa of storageNodes) {
    const name = getUniqueSanitizedName(sa.data.name);
    const accountTier = sa.data.accountTier || 'Standard';
    const replicationType = sa.data.replicationType || 'LRS';
    const rgNode = findParentRgForNode(sa, rgNodes, edges, errors);
    if (!rgNode) continue;
    const rgName = sanitize(rgNode.data.name);
    const saName = sa.data.name.replace(/[^a-z0-9]/gi, '').toLowerCase().slice(0, 24);
    lines.push(`resource "azurerm_storage_account" "${name}" {
  name                     = "${escapeHclString(saName)}"
  resource_group_name      = azurerm_resource_group.${rgName}.name
  location                 = azurerm_resource_group.${rgName}.location
  account_tier             = "${escapeHclString(accountTier)}"
  account_replication_type = "${escapeHclString(replicationType)}"
}
`);
  }

  for (const nsg of nsgNodes) {
    const name = getUniqueSanitizedName(nsg.data.name);
    const rgNode = findParentRgForNode(nsg, rgNodes, edges, errors);
    if (!rgNode) continue;
    const rgName = sanitize(rgNode.data.name);
    const rules = (nsg.data.securityRules || '').split('\n').filter((r) => r.trim());
    const ruleLines = rules.map((rule) => {
      const parts = rule.split('|');
      if (parts.length < 9) return '';
      const [rName, priority, direction, action, srcAddr, srcPort, dstAddr, dstPort, protocol] = parts;
      return `  security_rule {
    name                       = "${escapeHclString(rName)}"
    priority                   = ${parseInt(priority, 10) || 100}
    direction                  = "${escapeHclString(direction)}"
    access                     = "${escapeHclString(action)}"
    protocol                   = "${escapeHclString(protocol)}"
    source_port_range          = "${escapeHclString(srcPort)}"
    destination_port_range     = "${escapeHclString(dstPort)}"
    source_address_prefix      = "${escapeHclString(srcAddr)}"
    destination_address_prefix = "${escapeHclString(dstAddr)}"
  }`;
    }).filter(Boolean).join('\n');

    lines.push(`resource "azurerm_network_security_group" "${name}" {
  name                = "${escapeHclString(nsg.data.name)}"
  resource_group_name = azurerm_resource_group.${rgName}.name
  location            = azurerm_resource_group.${rgName}.location
${ruleLines}
}
`);

    const associatedSubnet = subnetNodes.find((s) => s.parentId === nsg.id) ||
      subnetNodes.find((s) => edges.some((e) => e.source === nsg.id && e.target === s.id));
    if (associatedSubnet) {
      const snetName = sanitize(associatedSubnet.data.name);
      const assocName = getUniqueSanitizedName(`${nsg.data.name}-assoc`);
      lines.push(`resource "azurerm_subnet_network_security_group_association" "${assocName}" {
  subnet_id                 = azurerm_subnet.${snetName}.id
  network_security_group_id = azurerm_network_security_group.${name}.id
}
`);
    }
  }

  for (const rt of routeTableNodes) {
    const name = getUniqueSanitizedName(rt.data.name);
    const rgNode = findParentRgForNode(rt, rgNodes, edges, errors);
    if (!rgNode) continue;
    const rgName = sanitize(rgNode.data.name);
    const routeEntries = (rt.data.routes || '').split('\n').filter((r) => r.trim());
    const routeLines = routeEntries.map((route) => {
      const parts = route.split('|');
      if (parts.length < 3) return '';
      const [rName, addrPrefix, nextHop] = parts;
      return `  route {
    name                   = "${escapeHclString(rName)}"
    address_prefix         = "${escapeHclString(addrPrefix)}"
    next_hop_type          = "${escapeHclString(nextHop)}"
  }`;
    }).filter(Boolean).join('\n');

    lines.push(`resource "azurerm_route_table" "${name}" {
  name                = "${escapeHclString(rt.data.name)}"
  resource_group_name = azurerm_resource_group.${rgName}.name
  location            = azurerm_resource_group.${rgName}.location
${routeLines}
}
`);

    const associatedSubnet = subnetNodes.find((s) => s.parentId === rt.id) ||
      subnetNodes.find((s) => edges.some((e) => e.source === rt.id && e.target === s.id));
    if (associatedSubnet) {
      const snetName = sanitize(associatedSubnet.data.name);
      const assocName = getUniqueSanitizedName(`${rt.data.name}-assoc`);
      lines.push(`resource "azurerm_subnet_route_table_association" "${assocName}" {
  subnet_id    = azurerm_subnet.${snetName}.id
  route_table_id = azurerm_route_table.${name}.id
}
`);
    }
  }

  for (const kv of kvNodes) {
    const name = getUniqueSanitizedName(kv.data.name);
    const kvSku = kv.data.kvSku || 'standard';
    const rgNode = findParentRgForNode(kv, rgNodes, edges, errors);
    if (!rgNode) continue;
    const rgName = sanitize(rgNode.data.name);
    const kvName = kv.data.name.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase().slice(0, 24);
    lines.push(`resource "azurerm_key_vault" "${name}" {
  name                = "${escapeHclString(kvName)}"
  resource_group_name = azurerm_resource_group.${rgName}.name
  location            = azurerm_resource_group.${rgName}.location
  tenant_id           = data.azurerm_client_config.current.tenant_id
  sku_name            = "${escapeHclString(kvSku)}"

  purge_soft_deleted_enabled = true
  purge_soft_delete_wait_duration = "PT10S"
}
`);
  }

  if (kvNodes.length > 0) {
    lines.unshift(`data "azurerm_client_config" "current" {}

`);
  }

  for (const app of appServiceNodes) {
    const name = getUniqueSanitizedName(app.data.name);
    const osType = (app.data.appOsType || 'Linux').toLowerCase();
    const runtime = app.data.appRuntime || 'NODE:18-lts';
    const rgNode = findParentRgForNode(app, rgNodes, edges, errors);
    if (!rgNode) continue;
    const rgName = sanitize(rgNode.data.name);
    const planName = getUniqueSanitizedName(`${app.data.name}-plan`);

    lines.push(`resource "azurerm_service_plan" "${planName}" {
  name                = "${escapeHclString(app.data.name + '-plan')}"
  resource_group_name = azurerm_resource_group.${rgName}.name
  location            = azurerm_resource_group.${rgName}.location
  os_type             = "${escapeHclString(osType)}"
  sku_name            = "B1"
}

resource "azurerm_${osType}_web_app" "${name}" {
  name                = "${escapeHclString(app.data.name)}"
  resource_group_name = azurerm_resource_group.${rgName}.name
  location            = azurerm_resource_group.${rgName}.location
  service_plan_id     = azurerm_service_plan.${planName}.id

  site_config {
    application_stack {
      ${osType === 'linux' ? `node_version = "${escapeHclString(runtime)}"` : `node_version = "${escapeHclString(runtime)}"`}
    }
  }
}
`);
  }

  for (const lb of lbNodes) {
    const name = getUniqueSanitizedName(lb.data.name);
    const lbSku = lb.data.lbSku || 'Standard';
    const lbType = lb.data.lbType || 'Public';
    const rgNode = findParentRgForNode(lb, rgNodes, edges, errors);
    if (!rgNode) continue;
    const rgName = sanitize(rgNode.data.name);
    const pipName = getUniqueSanitizedName(`${lb.data.name}-pip`);

    if (lbType === 'Public') {
      lines.push(`resource "azurerm_public_ip" "${pipName}" {
  name                = "${escapeHclString(lb.data.name + '-pip')}"
  resource_group_name = azurerm_resource_group.${rgName}.name
  location            = azurerm_resource_group.${rgName}.location
  allocation_method   = "Static"
  sku                 = "${escapeHclString(lbSku)}"
}
`);
    }

    lines.push(`resource "azurerm_lb" "${name}" {
  name                = "${escapeHclString(lb.data.name)}"
  resource_group_name = azurerm_resource_group.${rgName}.name
  location            = azurerm_resource_group.${rgName}.location
  sku                 = "${escapeHclString(lbSku)}"

  ${lbType === 'Public' ? `frontend_ip_configuration {
    name                 = "PublicIPAddress"
    public_ip_address_id = azurerm_public_ip.${pipName}.id
  }` : `frontend_ip_configuration {
    name                          = "PrivateIPAddress"
    private_ip_address_allocation = "Dynamic"
    subnet_id                     = ${subnetNodes.length > 0 ? `azurerm_subnet.${sanitize(subnetNodes[0].data.name)}.id` : 'null'}
  }`}
}
`);
  }

  const seenPeerings = new Set();
  for (const edge of peeringEdges) {
    const vnetA = vnetNodes.find((v) => v.id === edge.source);
    const vnetB = vnetNodes.find((v) => v.id === edge.target);
    if (!vnetA || !vnetB) continue;

    const pairKey = [vnetA.id, vnetB.id].sort().join('-');
    if (seenPeerings.has(pairKey)) continue;
    seenPeerings.add(pairKey);

    const nameA = sanitize(vnetA.data.name);
    const nameB = sanitize(vnetB.data.name);
    const peerA = getUniqueSanitizedName(`${nameA}_to_${nameB}`);
    const peerB = getUniqueSanitizedName(`${nameB}_to_${nameA}`);

    lines.push(`resource "azurerm_virtual_network_peering" "${peerA}" {
  name                      = "${escapeHclString(vnetA.data.name + '-to-' + vnetB.data.name)}"
  resource_group_name       = azurerm_virtual_network.${nameA}.resource_group_name
  virtual_network_name      = azurerm_virtual_network.${nameA}.name
  remote_virtual_network_id = azurerm_virtual_network.${nameB}.id
  allow_virtual_network_access = true
  allow_forwarded_traffic      = true
}
`);
    lines.push(`resource "azurerm_virtual_network_peering" "${peerB}" {
  name                      = "${escapeHclString(vnetB.data.name + '-to-' + vnetA.data.name)}"
  resource_group_name       = azurerm_virtual_network.${nameB}.resource_group_name
  virtual_network_name      = azurerm_virtual_network.${nameB}.name
  remote_virtual_network_id = azurerm_virtual_network.${nameA}.id
  allow_virtual_network_access = true
  allow_forwarded_traffic      = true
}
`);
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  return { ok: true, hcl: lines.join('\n') };
}
